import React, { useState, useEffect } from "react";
import {
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  CreditCard,
  FileCheck,
  Award,
  ShieldCheck,
  ArrowRight
} from "lucide-react";

const getSectionIcon = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes("aadhar")) return <CreditCard className="w-5 h-5 text-emerald-400" />;
  if (t.includes("pan")) return <FileCheck className="w-5 h-5 text-teal-400" />;
  if (t.includes("license") || t.includes("licence") || t.includes("dl")) return <Award className="w-5 h-5 text-amber-400" />;
  return <ShieldCheck className="w-5 h-5 text-emerald-400" />;
};

const KYCSection = ({ title, verified, fields, onSubmit }) => {
  const [showForm, setShowForm] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (verified) {
      setShowForm(false);
    }
  }, [verified]);

  const handleChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const processedValues = { ...formValues };
      // Automatically format YYYY-MM-DD date picker output to YYYYMMDD required by ULIP
      if (processedValues.dob && typeof processedValues.dob === "string") {
        processedValues.dob = processedValues.dob.replace(/-/g, "");
      }
      await onSubmit(processedValues);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFieldInput = (field) => {
    const isDateField = field.type === "date" || field.key === "dob";
    const isSelectField = field.type === "select" || field.key === "gender";
    const isPanField = field.key === "panno";
    const isAadharField = field.key === "uid";
    const isDlField = field.key === "dlnumber";
    const isMobileField = field.key === "mobile";

    if (isSelectField) {
      const options = field.options || [
        { label: "-- Select Gender --", value: "" },
        { label: "Male (M)", value: "M" },
        { label: "Female (F)", value: "F" },
        { label: "Other", value: "O" }
      ];

      return (
        <select
          required
          className="w-full bg-slate-950/80 border border-emerald-500/30 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 transition cursor-pointer"
          value={formValues[field.key] || ""}
          onChange={(e) => handleChange(field.key, e.target.value)}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-slate-900 text-white">
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (isDateField) {
      return (
        <div>
          <input
            type="date"
            required
            style={{ colorScheme: "dark" }}
            className="w-full bg-slate-950/80 border border-emerald-500/30 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 transition cursor-pointer"
            value={formValues[field.key] || ""}
            onChange={(e) => handleChange(field.key, e.target.value)}
          />
          <span className="text-[11px] text-emerald-300/80 mt-1 flex items-center gap-1 font-mono">
            <Clock className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            Format: YYYYMMDD {formValues[field.key] ? `(${formValues[field.key].replace(/-/g, "")})` : "(e.g. 19950815)"}
          </span>
        </div>
      );
    }

    let placeholder = field.placeholder || `Enter ${field.label}`;
    let helperText = field.helperText || "";

    if (isPanField) {
      placeholder = "e.g. ABCDE1234F";
      helperText = "Format: 5 Letters + 4 Digits + 1 Letter (e.g. ABCDE1234F)";
    } else if (isAadharField) {
      placeholder = "e.g. 548962103574";
      helperText = "Format: 12-Digit UID Number";
    } else if (isDlField) {
      placeholder = "e.g. DL1420110012345";
      helperText = "Format: State Code + Driving License Number";
    } else if (isMobileField) {
      placeholder = "e.g. 9876543210";
      helperText = "Format: 10-Digit Mobile Number";
    }

    return (
      <div>
        <input
          type={isMobileField ? "tel" : "text"}
          required
          placeholder={placeholder}
          maxLength={isPanField ? 10 : isAadharField ? 14 : isMobileField ? 10 : 40}
          className={`w-full bg-slate-950/70 border border-emerald-500/30 text-white placeholder-emerald-300/30 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 transition ${
            isPanField || isDlField ? "uppercase font-mono tracking-wider" : ""
          }`}
          value={formValues[field.key] || ""}
          onChange={(e) => {
            const val = isPanField || isDlField ? e.target.value.toUpperCase() : e.target.value;
            handleChange(field.key, val);
          }}
        />
        {helperText && (
          <span className="text-[10px] text-emerald-300/70 mt-1 block">
            {helperText}
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
        verified
          ? "bg-emerald-950/40 border-emerald-500/40 shadow-sm"
          : showForm
          ? "bg-[#0b2b22] border-emerald-400/60 shadow-xl shadow-emerald-950/50 ring-1 ring-emerald-500/30"
          : "bg-[#0f382c]/80 border-emerald-600/30 hover:border-emerald-500/50 hover:bg-[#124234]"
      }`}
    >
      {/* Header Bar */}
      <div className="p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center border shadow-inner ${
            verified ? "bg-emerald-500/20 border-emerald-400/30" : "bg-white/10 border-white/15"
          }`}>
            {getSectionIcon(title)}
          </div>
          <div>
            <h3 className="text-lg font-bold text-white tracking-wide">{title}</h3>
            <p className="text-xs text-emerald-200/70">
              {verified ? "Verification Completed" : "Instant verification available"}
            </p>
          </div>
        </div>

        <div>
          {verified ? (
            <span className="inline-flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/50 text-emerald-300 text-xs font-bold px-3.5 py-1.5 rounded-full shadow-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              Verified
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setShowForm((prev) => !prev)}
              className={`text-xs font-bold px-4 py-2 rounded-xl transition flex items-center gap-2 border ${
                showForm
                  ? "bg-red-500/20 border-red-400/40 text-red-300 hover:bg-red-500/30"
                  : "bg-emerald-500 hover:bg-emerald-400 text-slate-950 border-emerald-400 shadow-md"
              }`}
            >
              {showForm ? (
                <>
                  Cancel <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  Verify Now <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Expandable Form */}
      {showForm && !verified && (
        <div className="px-5 pb-6 pt-2 border-t border-emerald-800/40 bg-black/20">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {fields.map((field) => (
                <div key={field.key} className={fields.length === 1 ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-semibold text-emerald-200 uppercase tracking-wider mb-1.5">
                    {field.label}
                  </label>
                  {renderFieldInput(field)}
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-3 border-t border-emerald-800/30 mt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-bold px-6 py-2.5 rounded-xl shadow-lg transition transform hover:scale-[1.02] flex items-center justify-center gap-2 text-sm disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span>Verifying...</span>
                ) : (
                  <>
                    Submit Verification <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default KYCSection;
